import { ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';
import { PlaneView } from '~/engine/view/views/PlaneView';

/**
 * Per-frame data required to draw a single suggestion ghost.
 *
 * `polygonsVoxel` are in the texture grid's voxel-centered
 * continuous coordinate system (the output of marching squares).
 * `targetVoxelZ` is the Z voxel coordinate of the target slice in
 * the same grid. `textureGrid` is used to convert each polygon
 * vertex from voxel space to patient (world) space.
 *
 * `roiColor` is the structure color in [0,1] floats.
 */
export type ContourAutoSuggestionPreviewState = {
    polygonsVoxel: ReadonlyArray<ReadonlyArray<vec2>>;
    targetVoxelZ: number;
    textureGrid: Grid;
    roiColor: ReadonlyVec3;
};

/**
 * Renders a dashed "ghost" preview of a proposed contour onto the
 * 2D overlay canvas of the provided {@link PlaneView}. The renderer
 * intentionally owns no business logic — it is driven entirely by
 * {@link setState} / {@link clear} calls from the controller.
 *
 * The 2D overlay sits above the WebGL viewport, so re-rendering
 * on every view-state change (slice scroll, pan, zoom) is the
 * caller's responsibility.
 */
export class ContourAutoSuggestionPreviewRenderer {
    private static readonly DASH_PATTERN: ReadonlyArray<number> = [6, 4];
    private static readonly LINE_WIDTH = 1.5;

    private readonly planeView: PlaneView;
    private readonly canvasContext: CanvasRenderingContext2D | null;
    private current: ContourAutoSuggestionPreviewState | undefined;

    public constructor(planeView: PlaneView) {
        this.planeView = planeView;
        const canvas = this.planeView.get2DCanvas;
        if (!canvas) {
            throw new Error(
                'ContourAutoSuggestionPreviewRenderer: 2D canvas unavailable'
            );
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error(
                'ContourAutoSuggestionPreviewRenderer: 2D context unavailable'
            );
        }
        this.canvasContext = ctx;
    }

    public setState(state: ContourAutoSuggestionPreviewState): void {
        this.current = state;
        this.render();
    }

    /** Re-render with the current state (e.g. after a pan/zoom event). */
    public refresh(): void {
        if (this.current) {
            this.render();
        }
    }

    public clear(): void {
        this.current = undefined;
        const ctx = this.canvasContext;
        if (!ctx) {
            return;
        }
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    }

    public dispose(): void {
        this.clear();
    }

    private syncCanvasSize(): void {
        const canvas = this.planeView.get2DCanvas;
        const ctx = this.canvasContext;
        if (!canvas || !ctx) {
            return;
        }
        const w = canvas.offsetWidth;
        const h = canvas.offsetHeight;
        if (ctx.canvas.width !== w || ctx.canvas.height !== h) {
            ctx.canvas.width = w;
            ctx.canvas.height = h;
        }
    }

    private render(): void {
        const ctx = this.canvasContext;
        if (!ctx || !this.current) {
            return;
        }
        const { polygonsVoxel, targetVoxelZ, textureGrid, roiColor } =
            this.current;

        this.syncCanvasSize();
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

        const R = Math.round(roiColor[0] * 255);
        const G = Math.round(roiColor[1] * 255);
        const B = Math.round(roiColor[2] * 255);
        ctx.fillStyle = `rgba(${R}, ${G}, ${B}, 0.3)`;
        ctx.strokeStyle = `rgba(${R}, ${G}, ${B}, 0.8)`;
        ctx.lineWidth = ContourAutoSuggestionPreviewRenderer.LINE_WIDTH;
        ctx.setLineDash([
            ContourAutoSuggestionPreviewRenderer.DASH_PATTERN[0],
            ContourAutoSuggestionPreviewRenderer.DASH_PATTERN[1],
        ]);

        for (const poly of polygonsVoxel) {
            if (poly.length < 2) {
                continue;
            }
            ctx.beginPath();
            for (let i = 0; i < poly.length; i++) {
                const v = poly[i];
                const world = textureGrid.toWorldSpace(
                    vec3.fromValues(v[0], v[1], targetVoxelZ + 0.5)
                );
                const canvasCoords =
                    this.planeView.canvasCoordsFromPatientCoords(world);
                if (i === 0) {
                    ctx.moveTo(canvasCoords[0], canvasCoords[1]);
                } else {
                    ctx.lineTo(canvasCoords[0], canvasCoords[1]);
                }
            }
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
        }

        ctx.setLineDash([]);
    }
}
